import { useMemo } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { Announcement } from '@web/announcements/types/Announcement';
import {
  getAnnouncementsQueryKey,
  getAnnouncementDetailsQueryKey,
} from '@web/announcements/utils/getQueryKey';

export const useAnnouncementsQueryCache = () => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const markAnnouncementAsRead = (announcementId: ID) => {
      const queryKey = getAnnouncementsQueryKey();

      queryClient.setQueryData<InfiniteData<Announcement[]>>(queryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              pages: cachedData.pages.map((announcements) =>
                announcements.map((announcement) =>
                  announcement.id === announcementId
                    ? { ...announcement, read: true }
                    : announcement
                )
              ),
            }
          : undefined
      );
    };

    const setDeletedFlag = (id: ID, value = true) => {
      const queryKey = getAnnouncementsQueryKey();

      queryClient.setQueryData<InfiniteData<Announcement[]>>(queryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              pages: cachedData.pages.map((announcements) =>
                announcements.map((announcement) =>
                  announcement.id === id ? { ...announcement, isDeleted: value } : announcement
                )
              ),
            }
          : undefined
      );

      const detailsQueryKey = getAnnouncementDetailsQueryKey(id);

      queryClient.setQueryData<Announcement>(detailsQueryKey, (cachedData) => ({
        ...cachedData,
        isDeleted: value,
      }));
    };

    const updateRecipients = (id: ID, recipients: Announcement['recipients']) => {
      const queryKey = getAnnouncementsQueryKey();

      queryClient.setQueryData<InfiniteData<Announcement[]>>(queryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              pages: cachedData.pages.map((announcements) =>
                announcements.map((announcement) =>
                  announcement.id === id ? { ...announcement, recipients } : announcement
                )
              ),
            }
          : undefined
      );
    };

    return {
      markAnnouncementAsRead,
      setDeletedFlag,
      updateRecipients,
    };
  }, [queryClient]);
};
